import React, { useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import vmTheme from "../../../constants/theme";
import { SpeedVideoContainer } from "./propertywindow-components";
import { updateTimelineTime } from "../../../redux/actions/timelineUtils";
import GroupedButtons from "../../../common-components/GroupedButtons";

const itemHoverEffect = `
transform: scale(1.05);
transition: transform 0.2s ease-in-out;
`;

const inputActiveEffect = `
border: 2px solid #00BAFF;
background: #F0FBFE;
`;

const customeInputError = `
border: 2px solid #ED2B3E;
background: #F0FBFE;
`;

const buttonsArr = [
  {
    name: 0.5,
    value: "0.5x",
  },
  {
    name: 1,
    value: "1x",
  },
  {
    name: 1.5,
    value: "1.5x",
  },
  {
    name: 2,
    value: "2x",
  },
];

const speedList = [0.5, 1, 1.5, 2]

const VideoSpeed = () => {
  const dispatch = useDispatch();
  const [isCustomInputError, setCustomInputError] = useState(false);
  const workspaceItems = useSelector((state) => state.projectDetails);
  const appReducer = useSelector((state) => state.app);
  const selectedItem = appReducer.get("selectedItems");
  let itemId = selectedItem.get(0);
  let item = workspaceItems.getIn(["workspaceItems", itemId]);
  if (item && item.get("sceneData") && item.get("dropId")) {
    itemId = item.get("dropId");
    item = workspaceItems.getIn(["workspaceItems", itemId]);
  }
  const speed = item.get("speed");
  const theme = useSelector((state) => state.app.get("theme"));


  const onSave = useCallback((videoSpeed) => {
    const itemSpeed = parseFloat(videoSpeed);
    const currentSpeed = item.get("speed") || 1;
    const speedRatio = currentSpeed / itemSpeed;

    const videoStart = item.get("videoStart");
    const videoEnd = item.get("videoEnd");
    const videoDuration = videoEnd - videoStart;
    const updateVideoDuration = videoDuration / itemSpeed;

    const parentTimelineStart = item.get("enterStart");
    const updateExitEnd = parentTimelineStart + updateVideoDuration;

    // Scale transitions proportionally to avoid overlap issues if duration shrinks
    const entryDuration = (item.get("enterEnd") - item.get("enterStart")) * speedRatio;
    const exitDuration = (item.get("exitEnd") - item.get("exitStart")) * speedRatio;

    const updateEnterEnd = parentTimelineStart + entryDuration;
    const updateExitStart = updateExitEnd - exitDuration;

    const toUpdate = [];

    // Get current zoom data
    const zoomData = item.get("zoomData");
    let updatedZoomData = null;

    // Adjust zoom data timings based on speed change
    if (zoomData) {
      updatedZoomData = zoomData.mapEntries(([key, zoomGroup]) => {
        const updatedGroup = zoomGroup.mapEntries(([stageKey, stage]) => {
          // Adjust start and end times by the speed ratio
          const adjustedStage = stage.update('start', start => start * speedRatio)
            .update('end', end => end * speedRatio);
          return [stageKey, adjustedStage];
        });
        return [key, updatedGroup];
      });
    }

    const updateObj = {
      speed: itemSpeed,
      enterEnd: updateEnterEnd,
      exitStart: updateExitStart,
      exitEnd: updateExitEnd,
    };

    // Only include zoomData in update if it was modified
    if (updatedZoomData) {
      updateObj.zoomData = updatedZoomData;
    }

    // Update child reframe scenes based on the speed change
    const parentDropId = item.get("dropId") || item.get("id");
    const workspaceItemsMap = workspaceItems.get("workspaceItems");

    workspaceItemsMap.forEach((wsItem) => {
      // Identify children by dropId (excluding the parent itself)
      if (wsItem.get("dropId") === parentDropId && wsItem.get("id") !== item.get("id")) {
        const childOldEnterStart = wsItem.get("enterStart");
        const childOldEnterEnd = wsItem.get("enterEnd");
        const childOldExitStart = wsItem.get("exitStart");
        const childOldExitEnd = wsItem.get("exitEnd");

        // Scale timeline positions relative to parent start using the speed ratio
        const childNewEnterStart = parentTimelineStart + (childOldEnterStart - parentTimelineStart) * speedRatio;
        const childNewEnterEnd = parentTimelineStart + (childOldEnterEnd - parentTimelineStart) * speedRatio;
        const childNewExitStart = parentTimelineStart + (childOldExitStart - parentTimelineStart) * speedRatio;
        const childNewExitEnd = parentTimelineStart + (childOldExitEnd - parentTimelineStart) * speedRatio;

        toUpdate.push({
          id: wsItem.get("id"),
          container: "workspaceItems",
          toUpdate: {
            enterStart: childNewEnterStart,
            enterEnd: childNewEnterEnd,
            exitStart: childNewExitStart,
            exitEnd: childNewExitEnd,
            speed: itemSpeed,
          },
        });
      }
    });

    // For transitions, Need to remove enterId and exitId if applied speed
    if (item.get("transitionEnterId") !== "none" && itemSpeed > 1) {
      const nextItem = workspaceItems.getIn(["workspaceItems", item.get("transitionEnterId")])
      if (nextItem) {
        toUpdate.push({
          id: itemId,
          container: "workspaceItems",
          toUpdate: {
            transitionEnterId: "none"
          },
        })
        toUpdate.push({
          id: nextItem.get("id"),
          container: "workspaceItems",
          toUpdate: {
            transitionExitId: "none"
          },
        })
      }
    } else {
      workspaceItems.get("workspaceItems").forEach((workspaceItem) => {
        if (workspaceItem.get("id") !== item.get("id") &&
          workspaceItem.get("track") === item.get("track") &&
          workspaceItem.get("enterStart") < updateExitEnd &&
          workspaceItem.get("exitEnd") > parentTimelineStart) {
          // Logic for handling overlaps could be added here
        }
      });
    }

    toUpdate.push({
      id: itemId,
      container: "workspaceItems",
      toUpdate: updateObj,
    })

    if (toUpdate.length) {
      dispatch(updateTimelineTime({ toUpdate }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  const onChange = (value) => {
    if (value < 0.5 || value > 10) {
      setCustomInputError(true);
    } else if (value <= 10) {
      onSave(value);
      setCustomInputError(false);
    }
  }

  return (
    <SpeedVideoContainer>
      <GroupedButtons
        buttonsArray={buttonsArr}
        buttonDefaultColor={"#FFFFFF"}
        showHoverColor={false}
        hoverEffects={itemHoverEffect}
        activeEffects={isCustomInputError ? customeInputError : inputActiveEffect}
        onClick={onSave}
        buttonWidth={"49px"}
        onChange={onChange}
        isMultiSelect={false}
        fontColor={vmTheme[theme].panelPrimaryColor}
        inputType={"number"}
        selectedButton={speed || 1}
        enableCustomInput={true}
        isCustomInput={speed && !speedList.includes(speed)}
        borderRadius={"6px"}
      />
    </SpeedVideoContainer>
  );
};

export default VideoSpeed;
